import React, {useCallback, useEffect} from 'react';
import {
  ScrollView,
  TouchableOpacity,
  View,
  useWindowDimensions,
  StyleSheet,
  Keyboard,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Text from '../../../components/Text';
import * as yup from 'yup';
import Button from '../../../components/Button';
import Heading from '../../../components/Heading';
import HStack from '../../../components/HStack';
import Icon from '@expo/vector-icons/Feather';
import RegisterActionBar from '../../../components/RegisterActionBar';
import RegisterPrevButton from '../../../components/RegisterPrevButton';
import {useDispatch, useSelector} from 'react-redux';
import {RegistrationData} from '../types';
import {resetRegisterData, setRegisterData} from '../registerReducer';
import ScreenWrapper from '../components/ScreenWrapper';
import isWeb from '../../../utils/isWeb';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import TextInput from '../../../components/TextInput';
import shouldAutoFocus from '../../../utils/shouldAutoFocus';
import {checkEmail, emailUsername} from '../api';
import ConfirmationDialog from '../../../components/ConfirmationDialogJS';
import {useFocusEffect, useRoute} from '@react-navigation/native';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../service/flashMessage';
import {RootStore} from '../../../redux/store';
import {store} from '../../../redux/store';
import {dismissKeyboardThen} from '../../../utils/keyboard';

const emailValidationSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email'),
});

const EmailCheck = ({navigation}) => {
  const {params} = useRoute();
  // @ts-ignore
  const shouldReset = !!params?.shouldReset;

  const dispatch = useDispatch();
  const {width} = useWindowDimensions();

  const registerValues =
    useSelector((state: RootStore) => state?.register) || {};
  const email = registerValues?.email || '';
  const {control, handleSubmit} = useForm({
    defaultValues: {
      email,
    },
    resolver: yupResolver(emailValidationSchema),
  });

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const closeDialog = () => setDialogOpen(false);
  const [usernameMatchesEmail, setUsernameMatchesEmail] = React.useState(null);

  const [loading, setLoading] = React.useState(false);
  const [sendingUsername, setSendingUsername] = React.useState(false);

  const [isOpen, setIsOpen] = React.useState(false);

  const onClose = () => setIsOpen(false);

  const onYesPressed = () => {
    navigation.goBack();
  };

  const onNoPressed = () => {
    console.log('No');
  };

  const handleEmailSubmit = async (values: {email: string}) => {
    Keyboard.dismiss();
    setLoading(true);
    const emailExists = await checkEmail(values.email);

    //@ts-ignore
    dispatch(setRegisterData({emailExists: emailExists.emailExists}));

    setLoading(false);
    if (emailExists.emailExists) {
      setUsernameMatchesEmail(emailExists.asUserName);
      setDialogOpen(true);
      return;
    }

    const registrationData: RegistrationData = {
      email: values.email,
    };
    //@ts-ignore
    dispatch(setRegisterData(registrationData));
    navigation.navigate('Phone');
  };

  const continueWithDuplicateEmail = () => {
    const registrationData: RegistrationData = {
      email: control._formValues.email,
    };
    //@ts-ignore
    dispatch(setRegisterData(registrationData));
    navigation.navigate('Phone');
  };

  const sendUsername = async (email: string) => {
    setSendingUsername(true);
    try {
      await emailUsername(email);
      showSuccessMessage(`Username sent to ${email}`);
    } catch (error) {
      showErrorMessage('EmailCheck: ' + error.message);
    } finally {
      setSendingUsername(false);
    }
  };

  useFocusEffect(
    useCallback(() => {
      // if user is navigating from Phone screen  to this screen
      // They are changing their email address so NO NEED to RESET the register data
      if (shouldReset) {
        //@ts-ignore
        dispatch(resetRegisterData());
      }
    }, [shouldReset, dispatch]),
  );

  useEffect(() => {
    if (shouldReset) {
      //@ts-ignore
      dispatch(resetRegisterData());
    }
  }, [shouldReset, dispatch]);

  useEffect(() => {
    store.dispatch({ type: 'EmailCheck', emailInUseModalVisible: dialogOpen });
  }, [dialogOpen]);

  return (
    <ScreenWrapper alignTop>
      <SafeAreaView
        style={[
          styles.safeAreaView,
          isWeb && {backgroundColor: '#ffffff', width: width / 3},
        ]}>
        <ScrollView
          style={styles.scrollView}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.scrollContent}>
          <RegisterActionBar step={1} />
          <View style={styles.view}>
            <Heading size="lg">Email</Heading>
            <Text style={styles.text}>
              Type in a valid email address. We will send you a code to confirm
              your email.
            </Text>
            {/* <Text style={styles.text}>The code is case sensitive.</Text> */}
            <TextInput
              //@ts-ignore
              control={control}
              name="email"
              placeholder="e.g johndoe@xyz.com"
              label="Email"
              keyboardType="email-address"
              autoCapitalize="none"
              InputRightElement={
                <TouchableOpacity style={styles.icon}>
                  <Icon name={'mail'} size={24} color="grey" />
                </TouchableOpacity>
              }
              autoFocus={shouldAutoFocus(true)}
            />
          </View>
        </ScrollView>
        <HStack style={styles.hStack}>
          <RegisterPrevButton
            onPress={() => {
              dismissKeyboardThen(() => setIsOpen(true));
            }}
          />
          <Button
            isLoading={loading}
            isLoadingText="Checking..."
            onPress={handleSubmit(handleEmailSubmit)}
            style={styles.button}>
            Next
          </Button>
        </HStack>
      </SafeAreaView>
      <ConfirmationDialog
        isOpen={dialogOpen}
        onClose={closeDialog}
        onNoPressed={closeDialog}
        onYesPressed={() =>
          usernameMatchesEmail
            ? navigation.navigate('Sign In', {email: control._formValues.email})
            : navigation.navigate('Sign In')
        }
        title="Email is already in use"
        description={
          sendingUsername
            ? 'Sending username ...'
            : usernameMatchesEmail
              ? `A profile username already exists for this email. \nIf you continue, you can't use this as your username. \nLogin takes you to login page.`
              : 'Email is already in use, please select an option. \n- Edit email to use a different email or login to continue. \n- Email username sends your username to your email. \n- Login takes you to login page.'
        }
        negativeLabel={'EDIT EMAIL'}
        positiveLabel="LOGIN "
        centerButton
        centerButtonLabel={
          usernameMatchesEmail
            ? 'CONTINUE '
            : sendingUsername
              ? 'LOADING...'
              : 'CONTINUE'
        }
        onCenterButtonPressed={() => continueWithDuplicateEmail()}
      />
      <ConfirmationDialog
        isOpen={isOpen}
        onClose={onClose}
        onNoPressed={onNoPressed}
        onYesPressed={onYesPressed}
        description="Are you sure you wish to abort registration? Your data will not be saved."
      />
    </ScreenWrapper>
  );
};

const styles = StyleSheet.create({
  safeAreaView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
    marginHorizontal: 8,
  },
  scrollContent: {
    flexGrow: 1,
  },
  view: {
    marginHorizontal: 24,
    marginTop: 16,
  },
  text: {
    marginTop: 16,
    fontSize: 18,
  },
  icon: {
    paddingHorizontal: 8,
  },
  hStack: {
    marginHorizontal: 24,
    marginTop: 'auto',
    marginBottom: 16,
  },
  button: {
    marginHorizontal: 8,
    flex: 1,
  },
});

export default EmailCheck;
